"use client";

import * as React from "react";
import { Contrast } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { useTheme } from "./theme-provider";

export interface ThemeToggleProps extends Omit<React.ComponentProps<typeof Button>, "onClick"> {}

/**
 * An icon button that toggles light/dark mode via `useTheme` (must live inside a `ThemeProvider`).
 * Built on the `Button` primitive — defaults to `variant="outline" size="icon-sm"`, both
 * overridable — with lucide's `Contrast` glyph (ADR-0005's icon source), filled via
 * `[&>path]:fill-current` because lucide ships it hollow. Pass `children` to swap the glyph.
 *
 * Shares its foundation with `PaletteToggle` (same `variant`/height scale) so the two — usually
 * placed side by side, e.g. a header — read as one visual language rather than two independent
 * style definitions.
 */
export function ThemeToggle({ variant = "outline", size = "icon-sm", className, children, ...props }: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={theme === "dark"}
      onClick={toggleTheme}
      data-slot="theme-toggle"
      className={cn(className)}
      {...props}
    >
      {children ?? <Contrast aria-hidden="true" className="[&>path]:fill-current" />}
    </Button>
  );
}
